import { DataBars, DataBarsRow } from "@/components/data-bars/DataBars";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { FormAggregatedResultElementProps } from "@/components/form-result/aggregated-result/components/types.ts";
import {
  Box,
  Button,
  FontAwesomeIcon,
  Stack,
  TabItem,
  Tabs,
  Text,
  Title,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export const FormAggregatedResultEmployeeNPSElement = ({
  element,
  result,
  showDetailModalCallback,
}: FormAggregatedResultElementProps) => {
  const scaleOptions = Array.from({ length: 11 }, (_, i) => i);
  const employee_nps_settings = element.employee_nps_settings;
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const [isShowBreakdown, setIsShowBreakdown] = useState(false);

  const rows = useMemo(() => {
    if (!result || !result.employee_nps_data) return null;

    const rowsInner: DataBarsRow[] = [];

    let sum = 0;
    for (const option of scaleOptions) {
      const selectOption = result.employee_nps_data.options.find(
        (x) => x.selectable_option_id === option
      );
      sum += selectOption?.count || 0;
    }

    for (const option of scaleOptions) {
      const selectOption = result.employee_nps_data.options.find(
        (x) => x.selectable_option_id === option
      );
      const count = selectOption?.count || 0;
      const percent = sum > 0 ? Math.round((100 * count) / sum) : 0;

      rowsInner.push({
        title: option.toString() || "-",
        emoji: null,
        value: count > 0 ? `${percent}%` : t("common:no_votes"),
        percent: percent,
        onClick: showDetailModalCallback
          ? () => showDetailModalCallback(element, { selectable_option_ids: [option] })
          : undefined,
        tooltip: (
          <Stack className={"text-sm"}>
            {count > 0 ? (
              <Text>{t("plurals:responses_count", { count: count })}</Text>
            ) : (
              <Text>{t("common:no_votes")}</Text>
            )}
            <Text className={"text-sm text-light/60"}>{t("common:click_to_view_answers")}</Text>
          </Stack>
        ),
      });
    }
    return rowsInner;
  }, [result]);

  const departmentRows: DataBarsRow[] =
    result?.employee_nps_data?.by_departments?.map((item) => ({
      title: item.department.title,
      emoji: null,
      value: item.nps_score > 0 ? `+${item.nps_score}` : item.nps_score,
      percent: item.nps_score,
      onClick: showDetailModalCallback
        ? () => showDetailModalCallback(element, { departments: [item.department] })
        : undefined,
      tooltip: (
        <Stack className={"text-sm"}>
          {t("surveys:employee_nps_score_label", { score: item.nps_score })}
          <Text className={"text-sm text-light/60"}>{t("common:click_to_view_answers")}</Text>
        </Stack>
      ),
      barClassName:
        item.nps_score >= 31 ? "bg-positive" : item.nps_score >= 0 ? "bg-warning" : "bg-danger",
      centered: true,
    })) ?? [];

  const managerRows: DataBarsRow[] =
    result?.employee_nps_data?.by_managers?.map((item) => ({
      title: renderEmployeeName(item.manager),
      emoji: null,
      value: item.nps_score > 0 ? `+${item.nps_score}` : item.nps_score,
      percent: item.nps_score,
      centered: true,
      onClick: showDetailModalCallback
        ? () => showDetailModalCallback(element, { managers: [item.manager] })
        : undefined,
      barClassName:
        item.nps_score >= 31 ? "bg-positive" : item.nps_score >= 0 ? "bg-warning" : "bg-danger",
      tooltip: (
        <Stack className={"text-sm"}>
          {t("surveys:employee_nps_score_label", { score: item.nps_score })}
          <Text className={"text-sm text-light/60"}>{t("common:click_to_view_answers")}</Text>
        </Stack>
      ),
    })) ?? [];

  const npsRows: DataBarsRow[] | null = result?.employee_nps_data
    ? [
        {
          title: t("surveys:employee_nps_promoters"),
          emoji: null,
          value: `${result.employee_nps_data.promoters_percent}%`,
          percent: result.employee_nps_data.promoters_percent,
          tooltip: t("plurals:responses_count", {
            count: result.employee_nps_data.promoters_count,
          }),
          onClick: showDetailModalCallback
            ? () => showDetailModalCallback(element, { selectable_option_ids: [9, 10] })
            : undefined,
        },
        {
          title: t("surveys:employee_nps_passives"),
          emoji: null,
          value: `${result.employee_nps_data.passives_percent}%`,
          percent: result.employee_nps_data.passives_percent,
          tooltip: t("plurals:responses_count", { count: result.employee_nps_data.passives_count }),
          onClick: showDetailModalCallback
            ? () => showDetailModalCallback(element, { selectable_option_ids: [7, 8] })
            : undefined,
        },
        {
          title: t("surveys:employee_nps_detractors"),
          emoji: null,
          value: `${result.employee_nps_data.detractors_percent}%`,
          percent: result.employee_nps_data.detractors_percent,
          tooltip: t("plurals:responses_count", {
            count: result.employee_nps_data.detractors_count,
          }),
          onClick: showDetailModalCallback
            ? () =>
                showDetailModalCallback(element, { selectable_option_ids: [0, 1, 2, 3, 4, 5, 6] })
            : undefined,
        },
      ]
    : null;

  if (!employee_nps_settings) return null;

  return (
    <Stack gap={"xl"}>
      <Stack gap={"md"}>
        <Title
          header={employee_nps_settings.title ?? t("surveys:employee_nps_element_title_default")}
          caption={employee_nps_settings.description ?? ""}
          required={employee_nps_settings.is_required}
          size={"sm"}
        />
        {result && result.employee_nps_data && (
          <Title
            header={`${result.employee_nps_data.nps_score ?? "-"}`}
            captionSlot={
              <Stack items={"center"} direction={"horizontal"} gap={"xs"}>
                <Text>
                  {(result.employee_nps_data.nps_score ?? 0) >= 31
                    ? t("surveys:employee_nps_rating_excellent")
                    : (result.employee_nps_data.nps_score ?? 0) >= 0
                      ? t("surveys:employee_nps_rating_good")
                      : t("surveys:employee_nps_rating_needs_improvement")}
                </Text>
                <Tooltip>
                  <TooltipTrigger>
                    <FontAwesomeIcon icon={"fa-light fa-question-circle"} />
                  </TooltipTrigger>
                  <TooltipContent>
                    <Stack className={"text-sm"} gap={"xs"}>
                      <Text>{t("surveys:employee_nps_how_measured")}</Text>
                      <Stack direction={"horizontal"} items={"center"} gap={"xs"}>
                        <Box className={"h-3 w-3 rounded-full bg-danger"} />
                        <Text>{t("surveys:employee_nps_range_negative")}</Text>
                      </Stack>
                      <Stack direction={"horizontal"} items={"center"} gap={"xs"}>
                        <Box className={"h-3 w-3 rounded-full bg-warning"} />
                        <Text>{t("surveys:employee_nps_range_good")}</Text>
                      </Stack>
                      <Stack direction={"horizontal"} items={"center"} gap={"xs"}>
                        <Box className={"h-3 w-3 rounded-full bg-positive"} />
                        <Text>{t("surveys:employee_nps_range_excellent")}</Text>
                      </Stack>
                    </Stack>
                  </TooltipContent>
                </Tooltip>
              </Stack>
            }
            size={"lg"}
          />
        )}
      </Stack>

      {result && result.employee_nps_data && (
        <>
          {npsRows && (
            <DataBars
              className={twMerge(showDetailModalCallback ? "cursor-pointer" : undefined, "p-0")}
              onClick={showDetailModalCallback ? () => showDetailModalCallback(element) : undefined}
              rows={npsRows}
            />
          )}
          <Box className={"print:hidden"}>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setIsShowBreakdown(!isShowBreakdown);
              }}
              variant={"secondary"}
            >
              {isShowBreakdown ? t("common:hide_breakdown") : t("common:show_breakdown")}
            </Button>
          </Box>
          {isShowBreakdown && rows && (
            <Tabs
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <TabItem title={t("surveys:employee_nps_tab_by_department")}>
                <DataBars rows={departmentRows} className={"py-8"} />
              </TabItem>
              <TabItem title={t("surveys:employee_nps_tab_by_manager")}>
                <DataBars rows={managerRows} className={"py-8"} />
              </TabItem>
              <TabItem title={t("surveys:employee_nps_tab_score_distribution")}>
                <DataBars
                  rows={rows}
                  onClick={
                    showDetailModalCallback ? () => showDetailModalCallback(element) : undefined
                  }
                  className={twMerge(
                    showDetailModalCallback ? "cursor-pointer" : undefined,
                    "py-8"
                  )}
                />
              </TabItem>
            </Tabs>
          )}
        </>
      )}
    </Stack>
  );
};
